<script>
import InputMixin from '@mixins/InputMixin'
import Language from '@enums/Language'
import OutreachProgramsRequests from '@requests/OutreachProgramsRequests'
import VoiceFormattingButton from '@components/QuestionForm/VoiceFormattingButton'
import { formatSecondsAsMinutes } from '@helpers/FormatHelper'
import { snakeCaseToCamelCaseKeepingSpecialCharacters } from '@helpers/StringHelper'
import { ttsAudioBlocksSave } from '@helpers/audioOrAutomatedVoiceHelpers'
import { uniqueId } from 'lodash'

const ENGLISH_LANGUAGE = 'english'
const HUMAN_VOICE = 'human_voice'
const TTS_SYNC_VALIDATION_ERROR = 'Please generate and play audio for the current text before saving.'

function normalizeAudioTalent (audioTalent) {
  if (audioTalent === 'Human Voice') return HUMAN_VOICE
  return audioTalent
}

function hasPersistedAudio (audio) {
  if (!audio) return false
  if (typeof audio === 'string' && audio) return true
  return Boolean(audio?.url)
}

function hasInSessionAudioFile (audio) {
  return Boolean(audio?.file)
}

function hasPlayableAudio (audio) {
  return hasPersistedAudio(audio) || hasInSessionAudioFile(audio)
}

// Seed generatedFromText in data() (before the immediate validation watcher)
// when text already has playable audio (persisted or in-session File).
function syncedGeneratedFromText ({ value, audioField, model }) {
  if (!value || !audioField || !model) return null

  const audioFieldProp = snakeCaseToCamelCaseKeepingSpecialCharacters(audioField)
  const audioValue = model[audioFieldProp]
  if (audioValue === undefined || !hasPlayableAudio(audioValue)) return null

  return value
}

export default {
  name: 'AutomatedVoiceFormInput',
  components: {
    VoiceFormattingButton,
  },
  mixins: [InputMixin],
  props: {
    value: { type: String, default: null },
    rows: { type: Number, default: 2 },
    audioField: { type: String, default: null },

    // Optional: Distinguishes the generated file when several models share one audio field
    // name, as answers do with response_audio_message. Without it every answer would
    // generate response_audio_message.wav and overwrite the others in the bucket.
    fileScope: { type: String, default: null },
    model: { type: Object, default: null },
    // The one Language Settings talent for this preview. Parents select it: the
    // question variant's language (answers have none of their own), the chosen
    // program's talent for custom playback, or the English talent for program audio.
    voiceTalent: { type: Object, default: null },
    persistGeneratedAudio: { type: Boolean, default: true },
    requireGenerateBeforeSave: { type: Boolean, default: true },
    previewHelperMessage: { type: String, default: null },
  },
  data () {
    return {
      textareaId: uniqueId('automated-voice-form-input-'),
      generatedFromText: syncedGeneratedFromText({
        value: this.value,
        audioField: this.audioField,
        model: this.model,
      }),
      // Safety net if exclusivity clears model audio while this input stays mounted.
      generatedAudioFile: null,
      isGenerating: false,
      isPlaying: false,
      currentTime: 0,
      duration: null,
      playbackAudio: null,
      playbackAudioUrl: null,
      playbackError: null,
      seekBarWidth: 0,
    }
  },
  computed: {
    textLabel () {
      if (this.label === false) return false
      return this.label || 'Generated Audio Script'
    },
    // The uploaded object is stored under this name, so it has to be unique among siblings
    // sharing the audio field. Stable for the field, so regenerating overwrites its own
    // file rather than orphaning it.
    generatedFileName () {
      return [this.fileScope, this.audioField].filter(Boolean).join('_') + '.wav'
    },
    audioFieldProp () {
      return this.audioField ? snakeCaseToCamelCaseKeepingSpecialCharacters(this.audioField) : null
    },
    modelAudioValue () {
      if (!this.model || !this.audioFieldProp) return null
      const value = this.model[this.audioFieldProp]
      return value === undefined ? null : value
    },
    audioValue () {
      if (hasPlayableAudio(this.modelAudioValue)) return this.modelAudioValue
      return this.generatedAudioFile
    },
    voiceLanguage () {
      return this.voiceTalent?.language || ENGLISH_LANGUAGE
    },
    // Changes when the parent swaps talents or edits voice/tone, so a stale preview
    // is not played with a different voice.
    voiceSignature () {
      const talent = this.voiceTalent
      if (!talent) return null
      return [
        talent.language,
        normalizeAudioTalent(talent.audioTalent ?? talent.audio_talent),
        talent.toneProfile ?? talent.tone_profile ?? null,
      ].join(':')
    },
    voice () {
      if (!this.voiceTalent) return null
      return normalizeAudioTalent(this.voiceTalent.audioTalent ?? this.voiceTalent['audio_talent'])
    },
    toneProfile () {
      return this.voiceTalent?.toneProfile ?? this.voiceTalent?.['tone_profile'] ?? null
    },
    canGenerateAudio () {
      if (this.disabled || !this.value?.trim()) return false
      if (!this.voice || this.voice === HUMAN_VOICE) return false
      return Boolean(this.toneProfile)
    },
    needsGeneration () {
      if (!this.value?.trim()) return true
      if (this.persistGeneratedAudio) {
        if (!hasPersistedAudio(this.audioValue) && !this.generatedFromText) return true
      } else if (!this.generatedFromText) {
        return true
      }
      return this.value !== this.generatedFromText
    },
    ttsSyncValidationError () {
      // Hidden/disabled while Upload is active — do not block upload saves.
      if (this.disabled || !this.requireGenerateBeforeSave) return null
      return this.blocksSaveForTtsSync() ? TTS_SYNC_VALIDATION_ERROR : null
    },
    showCautionMessage () {
      return Boolean(this.ttsSyncValidationError)
    },
    playButtonLabel () {
      if (this.isGenerating) return 'Generating...'
      if (this.isPlaying) return 'Pause'
      return this.needsGeneration ? 'Generate and Play' : 'Play'
    },
    playButtonIcon () {
      if (this.isGenerating) return null
      return this.isPlaying ? 'pause' : 'play'
    },
    isPlayButtonDisabled () {
      if (this.disabled || this.isGenerating) return true
      if (this.needsGeneration) return !this.canGenerateAudio
      return !hasPlayableAudio(this.audioValue)
    },
    seekBarFraction () {
      if (!this.duration) return 0
      return this.currentTime / this.duration
    },
    canScrub () {
      return Boolean(this.playbackAudio) && Boolean(this.duration)
    },
    timerDisplay () {
      if (!this.duration) return '--:--'
      return formatSecondsAsMinutes(this.currentTime.toFixed(0))
    },
    canDeleteGeneratedAudio () {
      return hasPlayableAudio(this.audioValue) && Boolean(this.value?.trim())
    },
    languageLabel () {
      const match = Language.all.find(option => option.value === this.voiceLanguage)
      return match ? match.label : 'English'
    },
    reviewMessage () {
      return this.previewHelperMessage || 'Please review the AI-generated audio message carefully before it is sent out.'
    },
    voiceConfigHint () {
      if (this.disabled || !this.value?.trim() || this.canGenerateAudio) return null
      if (!this.voiceTalent) return null
      const label = this.languageLabel
      if (!this.voice || this.voice === HUMAN_VOICE) {
        return `Select a Gemini voice (not Human Voice) for ${label} in Language Settings.`
      }
      if (!this.toneProfile) {
        return `Select a tone profile for ${label} in Language Settings.`
      }
      return null
    },
  },
  watch: {
    value (newValue) {
      if (this.generatedFromText && newValue !== this.generatedFromText) {
        this.stopPlayback()
      }
      if (!newValue?.trim()) {
        this.generatedFromText = null
        this.generatedAudioFile = null
      }
    },
    disabled (isDisabled) {
      if (isDisabled) return
      this.reapplyGeneratedAudioToModel()
    },
    voiceSignature () {
      this.resetPreviewForVoiceChange()
    },
    ttsSyncValidationError: {
      immediate: true,
      handler (error) {
        this.$emit('input:customErrors', error)
      },
    },
  },
  beforeDestroy () {
    this.$emit('input:customErrors', null)
    this.stopPlayback()
  },
  methods: {
    blocksSaveForTtsSync () {
      return ttsAudioBlocksSave({
        mode: 'tts',
        ttsValue: this.value,
        generatedFromText: this.generatedFromText,
        audioValue: this.audioValue,
      })
    },
    reapplyGeneratedAudioToModel () {
      if (!this.persistGeneratedAudio) return
      if (!this.generatedAudioFile || !this.model || !this.audioFieldProp) return
      if (hasPlayableAudio(this.modelAudioValue)) return

      this.$set(this.model, this.audioFieldProp, this.generatedAudioFile)
      this.$emit('change')
    },
    resetPreviewForVoiceChange () {
      this.stopPlayback()
      this.generatedFromText = null
      this.generatedAudioFile = null
      this.playbackError = null
    },
    clearGeneratedAudio () {
      this.stopPlayback()
      this.generatedFromText = null
      this.generatedAudioFile = null
      this.playbackError = null

      if (this.model && this.audioFieldProp) {
        this.$set(this.model, this.audioFieldProp, null)
      }

      const panel = this.findAudioOrAutomatedPanel()
      if (panel) panel.clearGeneratedContentStashes()

      this.$emit('input', null)
      this.$emit('change')
    },
    findAudioOrAutomatedPanel () {
      let parent = this.$parent
      while (parent) {
        if (parent.$options.name === 'AudioOrAutomatedPanel') return parent
        parent = parent.$parent
      }
      return null
    },
    resetGeneratedVoiceState () {
      this.stopPlayback()
      this.generatedFromText = null
      this.generatedAudioFile = null
      this.playbackError = null
      this.$emit('input:customErrors', null)
    },
    onInput (event) {
      this.playbackError = null
      this.$emit('input', event.target.value)
    },
    stopPlayback () {
      if (this.playbackAudio) {
        this.playbackAudio.onended = null
        this.playbackAudio.onerror = null
        this.playbackAudio.ontimeupdate = null
        this.playbackAudio.pause()
        this.playbackAudio.currentTime = 0
        this.playbackAudio = null
      }
      if (this.playbackAudioUrl) {
        URL.revokeObjectURL(this.playbackAudioUrl)
        this.playbackAudioUrl = null
      }
      this.isPlaying = false
      this.currentTime = 0
      this.duration = null
    },
    setModelAudioFromBlob (blob) {
      const file = new File([blob], this.generatedFileName, { type: 'audio/wav' })
      const audioValue = { name: file.name, file }
      this.generatedAudioFile = audioValue
      this.generatedFromText = this.value
      this.playbackError = null

      if (this.persistGeneratedAudio && this.model && this.audioFieldProp) {
        this.$set(this.model, this.audioFieldProp, audioValue)
        const panel = this.findAudioOrAutomatedPanel()
        if (panel) panel.clearUploadedAudioStash()
      }

      this.$emit('change')
    },
    isAudioBlob (blob) {
      return blob instanceof Blob && blob.size > 0 && (
        blob.type.startsWith('audio/') || blob.type === 'application/octet-stream' || !blob.type
      )
    },
    async readBlobErrorMessage (blob) {
      try {
        const text = await blob.text()
        const parsed = JSON.parse(text)
        return parsed.message || 'Unable to generate audio.'
      } catch {
        return 'Unable to generate audio.'
      }
    },
    async extractPlaybackErrorMessage (error) {
      const data = error?.response?.data
      if (data instanceof Blob) return this.readBlobErrorMessage(data)
      if (data?.message) return data.message
      return 'Unable to generate audio. Check the server logs and try again.'
    },
    playAudioElement (audio, url, { revoke = false } = {}) {
      return new Promise((resolve, reject) => {
        this.attachAudioListeners(audio, url, { revoke })

        const startPlayback = async () => {
          try {
            await audio.play()
            this.isPlaying = true
            this.duration = audio.duration || this.duration
            this.currentTime = audio.currentTime
            resolve()
          } catch (error) {
            this.stopPlayback()
            reject(error)
          }
        }

        if (audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
          startPlayback()
          return
        }

        audio.addEventListener('canplay', () => { startPlayback() }, { once: true })
        audio.addEventListener('error', () => {
          this.stopPlayback()
          reject(new Error('Unable to play generated audio.'))
        }, { once: true })
      })
    },
    async loadPlaybackSource () {
      if (this.audioValue?.file) {
        const url = URL.createObjectURL(this.audioValue.file)
        return { url, revoke: true }
      }

      if (this.audioValue?.url) {
        return { url: `${this.audioValue.url}${this.audioValue.url.includes('?') ? '&' : '?'}play=true`, revoke: false }
      }

      return null
    },
    attachAudioListeners (audio, url, { revoke = false } = {}) {
      const clearPlaybackState = () => {
        if (revoke && this.playbackAudioUrl === url) {
          URL.revokeObjectURL(url)
          this.playbackAudioUrl = null
        }
        this.playbackAudio = null
        this.isPlaying = false
        this.currentTime = 0
      }

      audio.onended = clearPlaybackState
      audio.onerror = clearPlaybackState
      audio.ontimeupdate = () => {
        this.currentTime = audio.currentTime
      }
      audio.onloadedmetadata = () => {
        this.duration = audio.duration
      }

      this.playbackAudio = audio
      this.playbackAudioUrl = revoke ? url : null
    },
    async playCurrentAudio () {
      const source = await this.loadPlaybackSource()
      if (!source) return

      this.stopPlayback()
      this.playbackError = null

      const audio = new Audio(source.url)
      try {
        await this.playAudioElement(audio, source.url, { revoke: source.revoke })
      } catch {
        this.playbackError = 'Unable to play generated audio.'
      }
    },
    async generateAndPlayAudio () {
      if (!this.canGenerateAudio) return

      this.isGenerating = true
      this.playbackError = null
      this.stopPlayback()

      let generatedBlob = null
      try {
        const response = await OutreachProgramsRequests.previewGeminiTtsAudio({
          params: {
            query: {
              voice: this.voice,
              language: this.voiceLanguage,
              text: this.value,
              toneProfile: this.toneProfile,
            },
          },
          responseType: 'blob',
        })

        const blob = response.data
        if (!this.isAudioBlob(blob)) {
          this.playbackError = await this.readBlobErrorMessage(blob)
          return
        }

        generatedBlob = blob
        this.setModelAudioFromBlob(blob)
      } catch (error) {
        this.playbackError = await this.extractPlaybackErrorMessage(error)
      } finally {
        this.isGenerating = false
      }

      if (!generatedBlob || this.playbackError) return

      try {
        const url = URL.createObjectURL(generatedBlob)
        const audio = new Audio(url)
        await this.playAudioElement(audio, url, { revoke: true })
      } catch {
        this.playbackError = 'Unable to play generated audio.'
      }
    },
    seekToFraction (fraction) {
      if (!this.canScrub) return

      // Avoid landing exactly on the duration, which can immediately fire the audio's `ended` event.
      const clampedFraction = Math.min(Math.max(fraction, 0), 0.999)
      this.currentTime = clampedFraction * this.duration
      this.playbackAudio.currentTime = this.currentTime
    },
    onSeekDrag ({ phase, clientX, deltaX = 0 }) {
      if (!this.canScrub) return

      if (phase === 'start') {
        const { left, width } = this.$refs.seekBar.getBoundingClientRect()
        this.seekBarWidth = width
        this.seekToFraction((clientX - left) / width)
        return
      }

      if (!this.seekBarWidth) return
      this.seekToFraction(this.seekBarFraction + deltaX / this.seekBarWidth)
    },
    async onPlayButtonClick () {
      if (this.isPlayButtonDisabled) return

      if (this.isPlaying) {
        if (this.playbackAudio) this.playbackAudio.pause()
        this.isPlaying = false
        return
      }

      if (this.needsGeneration) {
        await this.generateAndPlayAudio()
        return
      }

      if (this.playbackAudio && this.playbackAudio.paused) {
        try {
          await this.playbackAudio.play()
          this.isPlaying = true
        } catch {
          this.stopPlayback()
        }
        return
      }

      await this.playCurrentAudio()
    },
  },
}
</script>

<template>
  <div class="text-input automated-voice-form-input">
    <Label v-if="textLabel" v-bind="{ required, hint }">
      {{ textLabel }}
      <template slot="labelExtras">
        <VoiceFormattingButton/>
      </template>
    </Label>
    <textarea
      :id="textareaId"
      v-autosize
      class="input textarea"
      :disabled="disabled"
      :placeholder="placeholder"
      :required="required"
      :rows="rows"
      :value="value"
      @input="onInput"
    />
    <InputError v-if="error && !showCautionMessage" :error="error"/>
    <div class="automated-voice-form-input__preview">
      <div class="automated-voice-form-input__preview-header">
        <Tag
          label="AI Generated Audio Sample"
          class="automated-voice-form-input__sample-tag"
        />
      </div>
      <div class="automated-voice-form-input__playback">
        <div
          ref="seekBar"
          v-drag="onSeekDrag"
          aria-hidden="true"
          class="automated-voice-form-input__seek-bar"
          :class="{ 'automated-voice-form-input__seek-bar--disabled': !canScrub }"
        >
          <div class="automated-voice-form-input__seek-bar-line">
            <div
              class="automated-voice-form-input__seek-bar-progress"
              :style="{ width: `${100 * seekBarFraction}%` }"
            />
            <div
              class="automated-voice-form-input__seek-bar-indicator"
              :style="{ left: `${100 * seekBarFraction}%` }"
            />
          </div>
        </div>
        <div
          aria-hidden="true"
          class="automated-voice-form-input__playback-timer"
        >{{ timerDisplay }}</div>
        <Button
          class="automated-voice-form-input__play-button"
          compact
          :disabled="isPlayButtonDisabled"
          :icon="playButtonIcon"
          :label="playButtonLabel"
          @click="onPlayButtonClick"
        />
        <Button
          v-if="canDeleteGeneratedAudio"
          class="glyphicon-trash automated-voice-form-input__delete-button"
          compact
          danger
          icon="delete"
          name="Delete Generated Audio"
          @click="clearGeneratedAudio"
        />
      </div>
    </div>
    <div
      v-if="voiceConfigHint"
      class="automated-voice-form-input__config-hint"
    >
      {{ voiceConfigHint }}
    </div>
    <div
      v-if="playbackError"
      class="automated-voice-form-input__playback-error"
    >
      {{ playbackError }}
    </div>
    <div
      v-if="showCautionMessage"
      class="automated-voice-form-input__review-message"
    >
      Please generate and play audio for the current text before saving.
    </div>
    <div
      v-else
      class="automated-voice-form-input__review-message automated-voice-form-input__review-message--info"
    >
      {{ reviewMessage }}
    </div>
  </div>
</template>

<style scoped lang="scss">
@include input;

.text-input {
  position: relative;
}

.input.textarea {
  display: block;
  resize: none;
}

.automated-voice-form-input__preview {
  @include bordered-box;

  margin-top: 16px;
  padding: 12px 16px;
}

.automated-voice-form-input__preview-header {
  border-bottom: $br-light;
  font-size: 12px;
  margin: -12px -16px 12px;
  padding: 8px;
}

.automated-voice-form-input__sample-tag {
  background-color: $WHITE;
  border-color: $cipher-green-l-0;
  color: $cipher-green-l-0;
  margin-bottom: 0;
}

.automated-voice-form-input__playback {
  align-items: center;
  display: flex;
  gap: 8px;
}

.automated-voice-form-input__seek-bar {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex: 1 1 auto;
  height: 16px; // Creates a larger hit area around the thin seek-bar-line for easier clicking/dragging.
  min-width: 0;

  &--disabled {
    cursor: default;
  }
}

.automated-voice-form-input__seek-bar-line {
  border-top: 2px solid $br-color-light;
  position: relative;
  width: 100%;
}

.automated-voice-form-input__seek-bar-progress {
  border-top: 2px solid $cipher-green-l-0;
  left: 0;
  margin-top: -2px;
  position: absolute;
  top: 0;
}

.automated-voice-form-input__seek-bar-indicator {
  background: $cipher-green-l-0;
  border-radius: 50%;
  cursor: grab;
  height: 10px;
  margin-left: -5px;
  margin-top: -6px;
  position: absolute;
  top: 0;
  width: 10px;

  .automated-voice-form-input__seek-bar--disabled & {
    cursor: default;
  }
}

.automated-voice-form-input__seek-bar:active .automated-voice-form-input__seek-bar-indicator {
  cursor: grabbing;
}

.automated-voice-form-input__playback-timer {
  color: $tundora-l-1;
  flex: 0 0 auto;
  font-size: $fs-secondary;
  min-width: 40px;
  text-align: right;
  user-select: none;
}

.automated-voice-form-input__play-button.button.secondary {
  background-color: $tundora-l-3;
  border-radius: 45px;
  color: $fc-html;
  flex-shrink: 0;
  font-size: $fs-normal;
  font-weight: $fw-bold;
  height: 28px;
  padding: 0 12px;
  width: auto;

  &:not([disabled]):focus,
  &:not([disabled]):hover {
    background-color: $tundora-l-2;
    border-color: $tundora-l-1;
  }
}

.automated-voice-form-input__delete-button {
  flex-shrink: 0;
}

.automated-voice-form-input__config-hint {
  color: $tundora-l-0;
  font-size: $fs-secondary;
  line-height: 1.4;
  margin-top: 8px;
}

.automated-voice-form-input__playback-error {
  background-color: $tundora-l-3;
  border-radius: 4px;
  color: $bg-error-color;
  font-size: $fs-secondary;
  line-height: 1.4;
  margin-top: 8px;
  padding: 8px 12px;
}

.automated-voice-form-input__review-message {
  background-color: $input-error-background-color;
  border: 1px solid $input-error-color;
  border-radius: 4px;
  color: $input-error-color;
  font-size: $fs-secondary;
  font-weight: $fw-bold;
  line-height: 1.4;
  margin: 4px 0;
  padding: 8px 12px;

  &--info {
    background-color: $tundora-l-3;
    border-color: transparent;
    color: $tundora-l-0;
    font-weight: $fw-regular;
  }
}
</style>
